/**
 * Textarea mit @-Mention-Autocomplete für Techniker.
 * Tippen von "@" öffnet ein Dropdown mit Techniker-Auswahl.
 * Ausgewählte Mentions werden als @Name in den Text eingefügt
 * und deren IDs zur Benachrichtigung weitergegeben.
 */
import { useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Textarea,
  Paper,
  Text,
  UnstyledButton,
  Badge,
  Group,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface MentionedUser {
  id: string;
  displayName: string;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onMentionsChange: (mentionedUserIds: string[]) => void;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  disabled?: boolean;
}

export function MentionTextarea({
  value,
  onChange,
  onMentionsChange,
  placeholder,
  minRows = 3,
  maxRows = 8,
  disabled,
}: MentionTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownFilter, setDropdownFilter] = useState('');
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionedUsers, setMentionedUsers] = useState<MentionedUser[]>([]);

  const { data: techData } = useQuery({
    queryKey: ['portal-technicians'],
    queryFn: () => api.getPortalTechnicians(),
    staleTime: 120_000,
  });

  const technicians = techData?.technicians ?? [];

  const filtered = dropdownFilter
    ? technicians.filter((t) =>
        t.displayName.toLowerCase().includes(dropdownFilter.toLowerCase())
      )
    : technicians;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.currentTarget.value;
      const cursor = e.currentTarget.selectionStart ?? newValue.length;
      onChange(newValue);

      // Detect @-trigger: find the last @ before cursor without space after it
      const textBeforeCursor = newValue.slice(0, cursor);
      const lastAt = textBeforeCursor.lastIndexOf('@');
      if (lastAt !== -1) {
        const afterAt = textBeforeCursor.slice(lastAt + 1);
        if (!afterAt.includes(' ') && !afterAt.includes('\n')) {
          setMentionStart(lastAt);
          setDropdownFilter(afterAt);
          setShowDropdown(true);
          return;
        }
      }
      setShowDropdown(false);
      setMentionStart(null);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (showDropdown && e.key === 'Escape') {
        setShowDropdown(false);
        e.preventDefault();
      }
    },
    [showDropdown]
  );

  function insertMention(tech: { id: string; displayName: string }) {
    if (mentionStart === null) return;
    const before = value.slice(0, mentionStart);
    const after = value.slice(mentionStart + 1 + dropdownFilter.length);
    const inserted = `@${tech.displayName} `;
    const newValue = before + inserted + after;
    onChange(newValue);

    const newMentions = mentionedUsers.some((m) => m.id === tech.id)
      ? mentionedUsers
      : [...mentionedUsers, { id: tech.id, displayName: tech.displayName }];
    setMentionedUsers(newMentions);
    onMentionsChange(newMentions.map((m) => m.id));

    setShowDropdown(false);
    setMentionStart(null);

    // Restore focus
    setTimeout(() => textareaRef.current?.focus(), 0);
  }

  function removeMention(userId: string) {
    const updated = mentionedUsers.filter((m) => m.id !== userId);
    setMentionedUsers(updated);
    onMentionsChange(updated.map((m) => m.id));
  }

  // Reset mentions when value is cleared externally
  useEffect(() => {
    if (!value) {
      setMentionedUsers([]);
      onMentionsChange([]);
    }
  }, [value]);

  return (
    <Box style={{ position: 'relative' }}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        placeholder={placeholder}
        minRows={minRows}
        maxRows={maxRows}
        disabled={disabled}
        autosize
      />

      {showDropdown && filtered.length > 0 && (
        <Paper
          withBorder
          shadow="md"
          style={{
            position: 'absolute',
            zIndex: 200,
            left: 0,
            right: 0,
            maxHeight: 200,
            overflowY: 'auto',
          }}
        >
          {filtered.slice(0, 8).map((t) => (
            <UnstyledButton
              key={t.id}
              style={{ display: 'block', width: '100%', padding: '6px 12px' }}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(t);
              }}
            >
              <Text size="sm">{t.displayName}</Text>
            </UnstyledButton>
          ))}
        </Paper>
      )}

      {mentionedUsers.length > 0 && (
        <Group gap={4} mt={4}>
          {mentionedUsers.map((m) => (
            <Badge
              key={m.id}
              size="xs"
              variant="light"
              color="teal"
              rightSection={
                <UnstyledButton
                  style={{ fontSize: 10, lineHeight: 1, marginLeft: 2 }}
                  onClick={() => removeMention(m.id)}
                >
                  ×
                </UnstyledButton>
              }
            >
              @{m.displayName}
            </Badge>
          ))}
        </Group>
      )}
    </Box>
  );
}
